<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert v-if="!isUserAdmin" type="error" outlined class="mt-8">
          You are not allowed to access background job controls.
        </v-alert>

        <v-card v-else class="mt-8 mb-8" :loading="loading">
          <template slot="progress">
            <v-progress-linear color="#006c27" indeterminate height="6"></v-progress-linear>
          </template>

          <v-card-title class="grey--text text--darken-2">
            Background Jobs
            <v-spacer />
            <v-btn color="#006c27" dark class="mr-2" @click="loadJobs">Reload</v-btn>
            <v-btn text color="#006c27" @click="loadAudit">Reload Audit</v-btn>
          </v-card-title>

          <v-card-text>
            <v-row class="mb-4" align="center">
              <v-col cols="12" md="8">
                <div class="text-subtitle-2">Interval format help</div>
                <div class="text-caption grey--text">
                  Use <code>@every 5m</code> for duration-based schedules or a fixed-time cron like
                  <code>0 2 * * *</code>.
                </div>
              </v-col>
              <v-col cols="12" md="4" class="d-flex justify-end">
                <v-btn
                  small
                  outlined
                  color="#006c27"
                  class="mr-2"
                  href="https://pkg.go.dev/time#ParseDuration"
                  target="_blank"
                >
                  @every help
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="#006c27"
                  href="https://pkg.go.dev/github.com/robfig/cron/v3#hdr-CRON_Expression_Format"
                  target="_blank"
                >
                  cron help
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="jobs"
              :loading="loading"
              item-key="jobId"
              class="elevation-1"
              :items-per-page="10"
            >
              <template v-slot:[`item.enabled`]="{ item }">
                <v-chip small :color="item.enabled ? '#006c27' : '#9e9e9e'" dark>
                  {{ item.enabled ? "enabled" : "disabled" }}
                </v-chip>
              </template>
              <template v-slot:[`item.running`]="{ item }">
                <v-chip small :color="item.running ? '#f57c00' : '#757575'" dark>
                  {{ item.running ? "running" : "idle" }}
                </v-chip>
              </template>
              <template v-slot:[`item.lastRun`]="{ item }">
                <div class="text-caption">
                  <div v-if="item.lastRun">
                    {{ runField(item.lastRun, "status") || "-" }} at
                    {{ formatDate(runField(item.lastRun, "finishedAt")) }}
                  </div>
                  <div v-else>-</div>
                </div>
              </template>
              <template v-slot:[`item.nextRunAtUtc`]="{ item }">
                <div class="text-caption">{{ formatDate(item.nextRunAtUtc) }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small color="#006c27" @click="openEdit(item)">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="#006c27"
                  :loading="runLoadingById[item.jobId] === true"
                  @click="openRunDialog(item)"
                >
                  <v-icon small>mdi-play-circle-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-divider class="my-6"></v-divider>

            <div class="text-subtitle-2 mb-3">Admin Change Audit (latest 50)</div>
            <v-data-table
              :headers="auditHeaders"
              :items="auditItems"
              :loading="auditLoading"
              item-key="id"
              :item-class="() => 'audit-row-clickable'"
              class="elevation-1"
              :items-per-page="10"
              @click:row="openAuditDetails"
            >
              <template v-slot:[`item.at`]="{ item }">
                <div class="text-caption">{{ formatDate(item.at) }}</div>
              </template>
              <template v-slot:[`item.reason`]="{ item }">
                <div class="text-caption">{{ item.reason || "-" }}</div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog" max-width="700">
      <v-card>
        <v-card-title class="text--secondary">Edit Job Configuration</v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-2">{{ editForm.displayName }}</div>
          <div class="text-caption grey--text mb-4">{{ editForm.description }}</div>

          <v-switch v-model="editForm.enabled" color="#006c27" label="Enabled"></v-switch>

          <v-text-field
            v-model="editForm.interval"
            label="Interval"
            hint="@every 5m or 0 2 * * *"
            :error-messages="editIntervalError ? [editIntervalError] : []"
            persistent-hint
            outlined
            dense
          ></v-text-field>

          <v-textarea
            v-model="editForm.reason"
            label="Reason (audit, required)"
            :error-messages="editReasonError ? [editReasonError] : (editReasonValidationError ? [editReasonValidationError] : [])"
            outlined
            rows="2"
            auto-grow
          ></v-textarea>

          <div class="d-flex justify-end">
            <v-btn
              small
              text
              color="#006c27"
              href="https://pkg.go.dev/time#ParseDuration"
              target="_blank"
              class="mr-2"
            >
              @every help
            </v-btn>
            <v-btn
              small
              text
              color="#006c27"
              href="https://pkg.go.dev/github.com/robfig/cron/v3#hdr-CRON_Expression_Format"
              target="_blank"
            >
              cron help
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <div v-if="!canSaveEdit" class="text-caption grey--text text--darken-1">{{ editActionHint }}</div>
          <v-spacer></v-spacer>
          <v-btn text :disabled="editLoading" @click="editDialog = false">Cancel</v-btn>
          <v-btn
            :color="canSaveEdit ? '#006c27' : '#9e9e9e'"
            :dark="canSaveEdit"
            :class="{ 'action-btn-disabled': !canSaveEdit }"
            :loading="editLoading"
            :disabled="!canSaveEdit"
            @click="saveEdit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="runDialog" max-width="560">
      <v-card>
        <v-card-title class="text--secondary">Run Job Now</v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-3">{{ runForm.displayName }}</div>
          <v-textarea
            v-model="runForm.reason"
            label="Reason (required)"
            :error-messages="runReasonError ? [runReasonError] : (runReasonValidationError ? [runReasonValidationError] : [])"
            outlined
            rows="3"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <div v-if="!canRunNow" class="text-caption grey--text text--darken-1">{{ runActionHint }}</div>
          <v-spacer></v-spacer>
          <v-btn text :disabled="runSubmitLoading" @click="closeRunDialog">Cancel</v-btn>
          <v-btn
            :color="canRunNow ? '#006c27' : '#9e9e9e'"
            :dark="canRunNow"
            :class="{ 'action-btn-disabled': !canRunNow }"
            :loading="runSubmitLoading"
            :disabled="!canRunNow"
            @click="confirmRunNow"
          >
            Run
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="auditDialog" max-width="980">
      <v-card>
        <v-card-title class="text--secondary">Audit Details</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6"><strong>Job:</strong> {{ auditSelected.jobId || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Action:</strong> {{ auditSelected.action || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Actor:</strong> {{ auditSelected.actor || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>At:</strong> {{ formatDate(auditSelected.at) }}</v-col>
            <v-col cols="12"><strong>Reason:</strong> {{ auditSelected.reason || "-" }}</v-col>
          </v-row>

          <div class="text-subtitle-2 mb-2">Changed fields</div>
          <v-alert v-if="auditDiffRows.length === 0" type="info" outlined dense>
            No field-level differences detected.
          </v-alert>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in auditDiffRows" :key="row.field">
                <td>{{ row.field }}</td>
                <td class="text-caption">{{ row.before }}</td>
                <td class="text-caption">{{ row.after }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <v-textarea :value="auditBeforePretty" label="Before JSON" outlined readonly auto-grow rows="8"></v-textarea>
            </v-col>
            <v-col cols="12" md="6">
              <v-textarea :value="auditAfterPretty" label="After JSON" outlined readonly auto-grow rows="8"></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="auditDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import cass from "../api/cass";

const REASON_MAX_LENGTH = 500;

export default {
  name: "BackgroundJobs",
  data() {
    return {
      loading: false,
      auditLoading: false,
      editLoading: false,
      runSubmitLoading: false,
      editDialog: false,
      runDialog: false,
      auditDialog: false,
      runLoadingById: {},
      jobs: [],
      auditItems: [],
      auditSelected: {},
      auditDiffRows: [],
      auditBeforePretty: "",
      auditAfterPretty: "",
      editReasonError: "",
      runReasonError: "",
      editForm: {
        jobId: "",
        displayName: "",
        description: "",
        enabled: false,
        interval: "",
        reason: "",
      },
      runForm: {
        jobId: "",
        displayName: "",
        reason: "",
      },
      headers: [
        { text: "Job", value: "displayName" },
        { text: "Enabled", value: "enabled", sortable: false },
        { text: "Interval", value: "interval" },
        { text: "Running", value: "running", sortable: false },
        { text: "Last run", value: "lastRun", sortable: false },
        { text: "Next run", value: "nextRunAtUtc", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      auditHeaders: [
        { text: "Time", value: "at" },
        { text: "Job", value: "jobId" },
        { text: "Action", value: "action" },
        { text: "Actor", value: "actor" },
        { text: "Reason", value: "reason", sortable: false },
      ],
    };
  },
  computed: {
    isUserAdmin() {
      return this.$userData && this.$userData.cass && this.$userData.cass.isUserAdmin;
    },
    editIntervalError() {
      const interval = (this.editForm.interval || "").trim();
      if (!interval) {
        return "Interval is required.";
      }
      if (!this.isValidScheduleExpression(interval)) {
        return "Use @every <duration> or a valid 5-field cron expression.";
      }
      return "";
    },
    editReasonValidationError() {
      return this.getReasonValidationError(this.editForm.reason);
    },
    runReasonValidationError() {
      return this.getReasonValidationError(this.runForm.reason);
    },
    canSaveEdit() {
      return (
        !this.editLoading &&
        !!this.editForm.jobId &&
        !this.editIntervalError &&
        !this.editReasonValidationError
      );
    },
    canRunNow() {
      return !this.runSubmitLoading && !!this.runForm.jobId && !this.runReasonValidationError;
    },
    editActionHint() {
      if (!this.editForm.jobId) {
        return "Select a job to continue.";
      }
      if (this.editIntervalError) {
        return this.editIntervalError;
      }
      if (this.editReasonValidationError) {
        return this.editReasonValidationError;
      }
      return "";
    },
    runActionHint() {
      if (!this.runForm.jobId) {
        return "Select a job to continue.";
      }
      if (this.runReasonValidationError) {
        return this.runReasonValidationError;
      }
      return "";
    },
  },
  watch: {
    "editForm.reason"() {
      this.editReasonError = "";
    },
    "runForm.reason"() {
      this.runReasonError = "";
    },
    "editForm.interval"() {
      this.editReasonError = "";
    },
  },
  mounted() {
    if (!this.isUserAdmin) {
      return;
    }
    this.loadJobs();
    this.loadAudit();
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleString();
    },
    runField(run, key) {
      if (!run) {
        return null;
      }
      if (Object.prototype.hasOwnProperty.call(run, key)) {
        return run[key];
      }
      const pascal = key.charAt(0).toUpperCase() + key.slice(1);
      if (Object.prototype.hasOwnProperty.call(run, pascal)) {
        return run[pascal];
      }
      return null;
    },
    normalizeReason(value) {
      return (value || "").trim();
    },
    getReasonValidationError(value) {
      const reason = this.normalizeReason(value);
      if (!reason) {
        return "Reason is required.";
      }
      if (reason.length > REASON_MAX_LENGTH) {
        return `Reason must be ${REASON_MAX_LENGTH} characters or fewer.`;
      }
      if (/[<>]/.test(reason)) {
        return "Reason must not contain angle brackets.";
      }
      for (let i = 0; i < reason.length; i += 1) {
        const code = reason.charCodeAt(i);
        const isAsciiControl = (code >= 0 && code <= 31) || code === 127;
        const isAllowedWhitespace = code === 9 || code === 10 || code === 13;
        if (isAsciiControl && !isAllowedWhitespace) {
          return "Reason must not contain control characters.";
        }
      }
      return "";
    },
    parseDurationToMs(durationText) {
      const unitMs = {
        ns: 0.000001,
        us: 0.001,
        "µs": 0.001,
        ms: 1,
        s: 1000,
        m: 60000,
        h: 3600000,
      };
      const tokenRegex = /(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g;
      let total = 0;
      let consumed = 0;
      let match = tokenRegex.exec(durationText);
      while (match) {
        const amount = Number(match[1]);
        const unit = match[2];
        total += amount * unitMs[unit];
        consumed += match[0].length;
        match = tokenRegex.exec(durationText);
      }
      if (consumed !== durationText.length) {
        return Number.NaN;
      }
      return total;
    },
    parseCronValue(value, min, max, nameMap) {
      const normalized = String(value).toUpperCase();
      if (nameMap && Object.prototype.hasOwnProperty.call(nameMap, normalized)) {
        return nameMap[normalized];
      }
      if (!/^\d+$/.test(normalized)) {
        return null;
      }
      const parsed = Number(normalized);
      if (Number.isNaN(parsed) || parsed < min || parsed > max) {
        return null;
      }
      return parsed;
    },
    isValidCronSegment(segment, min, max, nameMap = null) {
      const raw = (segment || "").trim();
      if (!raw) {
        return false;
      }
      const listItems = raw.split(",");
      for (const item of listItems) {
        const token = item.trim();
        if (!token) {
          return false;
        }
        const stepParts = token.split("/");
        if (stepParts.length > 2) {
          return false;
        }
        const base = stepParts[0];
        if (stepParts.length === 2) {
          if (!/^\d+$/.test(stepParts[1])) {
            return false;
          }
          const step = Number(stepParts[1]);
          if (Number.isNaN(step) || step <= 0) {
            return false;
          }
        }
        if (base === "*") {
          continue;
        }
        const rangeParts = base.split("-");
        if (rangeParts.length > 2) {
          return false;
        }
        const start = this.parseCronValue(rangeParts[0], min, max, nameMap);
        if (start === null) {
          return false;
        }
        if (rangeParts.length === 2) {
          const end = this.parseCronValue(rangeParts[1], min, max, nameMap);
          if (end === null || end < start) {
            return false;
          }
        }
      }
      return true;
    },
    isValidScheduleExpression(value) {
      const trimmed = (value || "").trim();
      if (!trimmed) {
        return false;
      }
      if (trimmed.startsWith("@every ")) {
        const durationText = trimmed.slice("@every ".length).trim();
        if (!durationText) {
          return false;
        }
        const ms = this.parseDurationToMs(durationText);
        return Number.isFinite(ms) && ms > 0;
      }
      if (trimmed.startsWith("@")) {
        return false;
      }
      const parts = trimmed.split(/\s+/);
      if (parts.length !== 5) {
        return false;
      }
      const monthNames = {
        JAN: 1,
        FEB: 2,
        MAR: 3,
        APR: 4,
        MAY: 5,
        JUN: 6,
        JUL: 7,
        AUG: 8,
        SEP: 9,
        OCT: 10,
        NOV: 11,
        DEC: 12,
      };
      const weekNames = {
        SUN: 0,
        MON: 1,
        TUE: 2,
        WED: 3,
        THU: 4,
        FRI: 5,
        SAT: 6,
      };
      return (
        this.isValidCronSegment(parts[0], 0, 59) &&
        this.isValidCronSegment(parts[1], 0, 23) &&
        this.isValidCronSegment(parts[2], 1, 31) &&
        this.isValidCronSegment(parts[3], 1, 12, monthNames) &&
        this.isValidCronSegment(parts[4], 0, 7, weekNames)
      );
    },
    loadJobs() {
      this.loading = true;
      cass
        .getBackgroundJobs()
        .then((response) => {
          this.jobs = response.data.items || [];
        })
        .catch((error) => {
          console.error("Could not load background jobs", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadAudit() {
      this.auditLoading = true;
      cass
        .getBackgroundJobAudit(50)
        .then((response) => {
          this.auditItems = response.data.items || [];
        })
        .catch((error) => {
          console.error("Could not load background job audit", error);
        })
        .finally(() => {
          this.auditLoading = false;
        });
    },
    openEdit(item) {
      this.editForm = {
        jobId: item.jobId,
        displayName: item.displayName,
        description: item.description,
        enabled: item.enabled,
        interval: item.interval,
        reason: "",
      };
      this.editDialog = true;
    },
    saveEdit() {
      const reason = this.normalizeReason(this.editForm.reason);
      const reasonError = this.getReasonValidationError(reason);
      if (reasonError) {
        this.editReasonError = reasonError;
        return;
      }
      if (this.editIntervalError) {
        this.editReasonError = this.editIntervalError;
        return;
      }
      this.editReasonError = "";
      this.editLoading = true;
      cass
        .patchBackgroundJob(this.editForm.jobId, {
          enabled: this.editForm.enabled,
          interval: this.editForm.interval,
          reason,
        })
        .then(() => {
          this.editDialog = false;
          this.loadJobs();
          this.loadAudit();
        })
        .catch((error) => {
          console.error("Could not update background job", error);
        })
        .finally(() => {
          this.editLoading = false;
        });
    },
    openRunDialog(item) {
      this.runForm = {
        jobId: item.jobId,
        displayName: item.displayName,
        reason: "",
      };
      this.runReasonError = "";
      this.runDialog = true;
    },
    closeRunDialog() {
      if (this.runSubmitLoading) {
        return;
      }
      this.runDialog = false;
    },
    confirmRunNow() {
      const trimmedReason = this.normalizeReason(this.runForm.reason);
      const reasonError = this.getReasonValidationError(trimmedReason);
      if (reasonError) {
        this.runReasonError = reasonError;
        return;
      }
      this.runReasonError = "";
      this.$set(this.runLoadingById, this.runForm.jobId, true);
      this.runSubmitLoading = true;
      cass
        .runBackgroundJob(this.runForm.jobId, trimmedReason)
        .then(() => {
          this.runDialog = false;
          this.loadJobs();
          this.loadAudit();
        })
        .catch((error) => {
          console.error("Could not trigger background job", error);
        })
        .finally(() => {
          this.$set(this.runLoadingById, this.runForm.jobId, false);
          this.runSubmitLoading = false;
        });
    },
    parseJSON(value) {
      if (!value || typeof value !== "string") {
        return {};
      }
      try {
        return JSON.parse(value);
      } catch (_error) {
        return {};
      }
    },
    isPlainObject(value) {
      return value !== null && typeof value === "object" && !Array.isArray(value);
    },
    valueToText(value) {
      if (typeof value === "undefined") {
        return "(missing)";
      }
      if (value === null) {
        return "null";
      }
      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch (_error) {
          return String(value);
        }
      }
      return String(value);
    },
    collectChanges(before, after, path, rows) {
      if (this.isPlainObject(before) && this.isPlainObject(after)) {
        const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
        keys.forEach((key) => {
          const nextPath = path ? `${path}.${key}` : key;
          this.collectChanges(before[key], after[key], nextPath, rows);
        });
        return;
      }
      const same = JSON.stringify(before) === JSON.stringify(after);
      if (same) {
        return;
      }
      rows.push({
        field: path || "(root)",
        before: this.valueToText(before),
        after: this.valueToText(after),
      });
    },
    openAuditDetails(item) {
      const before = this.parseJSON(item.beforeJson);
      const after = this.parseJSON(item.afterJson);
      const rows = [];
      this.collectChanges(before, after, "", rows);
      this.auditDiffRows = rows;
      this.auditBeforePretty = JSON.stringify(before, null, 2);
      this.auditAfterPretty = JSON.stringify(after, null, 2);
      this.auditSelected = item || {};
      this.auditDialog = true;
    },
  },
};
</script>

<style scoped>
.audit-row-clickable {
  cursor: pointer;
}

.action-btn-disabled {
  opacity: 0.9 !important;
}
</style>
